import React, { useCallback, useEffect, useMemo, useState } from "react";
import NumberFormat from "react-number-format";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Drawer } from "components/Measure-data/Drawer/Drawer";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useFormatNumericHook } from "hooks/shared-hooks/use-format-numeric-hook";
import { useDateFormatSelect, useDebtActions } from "store";
import { useDealDefaultCurrencySelect } from "store/selectors/deal-selectors/deal-selectors";
import {
  AmortizationColumn,
  AmortizationRow,
  CurrencyType,
  CustomPayment,
  FeeType,
  PrepaymentPayload
} from "store/types";

import { currencySign } from "../../constants";
import { useMeasureDrawer } from "../../hooks/debt-hooks/use-measure-drawer";
import type { PrepaymentFormData } from "./PrepaymentDrawer";
import { PrepaymentDrawerValues } from "./PrepaymentDrawerValues";

const textFieldStyles = {
  "& .MuiOutlinedInput-input": {
    padding: "8px 12px",
    fontSize: "14px",
    lineHeight: "16px",
    minHeight: 0,
    height: "32px",
    boxSizing: "border-box"
  }
};

const inputStyles = {
  "& .MuiOutlinedInput-input.MuiInputBase-input": {
    padding: "8px 12px",
    fontSize: "14px",
    lineHeight: "16px",
    minHeight: 0,
    height: "32px",
    boxSizing: "border-box"
  }
};

const boxStyles = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  bgcolor: "#ECF1F8",
  p: 2,
  borderRadius: 4
};

const titleStyles = {
  display: "flex",
  flexDirection: "row",
  gap: 2,
  mb: 2,
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid #e0e0e0"
};

interface PaymentConfirmationDrawerProps {
  open: boolean;
  onClose: (updated?: boolean) => void;
  dealId: number;
  amortizationRow: AmortizationRow | null;
  columns: AmortizationColumn[];
  mode: "approve" | "diff";
  actualPayment?: CustomPayment;
  prepayment?: CustomPayment;
}

interface ContentProps extends PaymentConfirmationDrawerProps {
  mode: "approve" | "diff";
}

interface PaymentFormData {
  effectiveDate: string;
  currency: CurrencyType;
  totalAmount: number;
  [key: string]: any; // Dynamic payment fields
}

interface PaymentRowProps {
  label: string;
  expectedValue: number;
  actualValue: number;
  currency: CurrencyType;
  mode: "approve" | "diff";
  formatNumber: (value: number) => string;
}

interface PaymentRowsStackProps {
  currency: CurrencyType;
  paymentColumns: AmortizationColumn[];
  formValues: PaymentFormData;
  mode: "approve" | "diff";
  formatNumber: (value: number) => string;
}

const getPaymentValue = (row: AmortizationRow | null, columnKey: string): number => {
  if (!row) return 0;
  return (row[columnKey as keyof AmortizationRow] as number) || 0;
};

const PaymentRow: React.FC<PaymentRowProps> = ({ label, expectedValue, actualValue, currency, mode, formatNumber }) => {
  const shortfall = expectedValue - actualValue;
  const getShortfallColor = () => {
    if (shortfall > 0) return "error.main"; // Underpayment (red)
    if (shortfall < 0) return "success.main"; // Overpayment (green)
    return "text.secondary"; // Match (default)
  };

  const formatShortfall = () => {
    const absShortfall = Math.abs(shortfall);
    const sign = shortfall > 0 ? "-" : shortfall < 0 ? "+" : "";
    return `(${sign}${currencySign[currency]}${formatNumber(absShortfall)})`;
  };

  return (
    <Box sx={boxStyles}>
      <Typography variant="body2" sx={{ minWidth: 80 }}>
        {label}
      </Typography>
      {mode === "approve" ? (
        <NumberFormat
          fullWidth
          thousandSeparator
          isNumericString
          variant="outlined"
          prefix={currencySign[currency]}
          decimalScale={2}
          disabled
          customInput={TextField}
          value={expectedValue}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "transparent"
            },
            ...inputStyles
          }}
        />
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
          <NumberFormat
            fullWidth
            thousandSeparator
            isNumericString
            variant="outlined"
            prefix={currencySign[currency]}
            decimalScale={2}
            disabled
            customInput={TextField}
            value={expectedValue}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "transparent"
              },
              ...inputStyles
            }}
          />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            /
          </Typography>
          <NumberFormat
            fullWidth
            thousandSeparator
            isNumericString
            variant="outlined"
            prefix={currencySign[currency]}
            decimalScale={2}
            disabled
            customInput={TextField}
            value={actualValue}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "transparent"
              },
              ...inputStyles
            }}
          />
          {shortfall !== 0 && (
            <Typography
              variant="body2"
              sx={{ color: getShortfallColor(), minWidth: "fit-content", whiteSpace: "nowrap" }}
            >
              {formatShortfall()}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const PaymentRowsStack: React.FC<PaymentRowsStackProps> = ({
  currency,
  paymentColumns,
  formValues,
  mode,
  formatNumber
}) => {
  return (
    <Stack spacing={2}>
      {paymentColumns.map((column) => {
        const expectedValue = formValues[`expected_${column.key}`] || 0;
        const actualValue = formValues[`actual_${column.key}`] || 0;

        // Skip rows with 0 expected value
        if (expectedValue === 0) {
          return null;
        }

        // Remove "Payment" from label
        const cleanLabel = column.label.replace(/\s*payment\s*/gi, "").trim();

        return (
          <PaymentRow
            key={column.key}
            label={cleanLabel}
            expectedValue={expectedValue}
            actualValue={actualValue}
            currency={currency}
            mode={mode}
            formatNumber={formatNumber}
          />
        );
      })}
    </Stack>
  );
};

const Content: React.FC<ContentProps> = ({
  onClose,
  dealId,
  amortizationRow,
  columns,
  mode,
  actualPayment,
  prepayment
}) => {
  const dealCurrency = useDealDefaultCurrencySelect();
  const dateFormat = useDateFormatSelect();
  const formatNumeric = useFormatNumericHook();

  const { createPrepaymentAction, updateCustomPaymentsAction } = useDebtActions();
  const { action: submitPrepayment, loading: submitting } = useActionHook(createPrepaymentAction);
  const { action: updateCustomPayment, loading: updating } = useActionHook(updateCustomPaymentsAction);

  const [isValuesDrawerOpen, setIsValuesDrawerOpen] = useState(false);
  const { rounds } = useMeasureDrawer([dealId]);

  // Filter and sort payment columns by priority
  const paymentColumns = useMemo(
    () =>
      columns
        .filter((col) => col.category === "payment")
        .sort((a, b) => (a.payment_priority || 0) - (b.payment_priority || 0)),
    [columns]
  );

  // Calculate expected values from row data
  const expectedValues = useMemo(() => {
    const values: Record<string, number> = {};
    let total = 0;
    paymentColumns.forEach((column) => {
      const value = getPaymentValue(amortizationRow, column.key);
      values[column.key] = value;
      total += value;
    });
    return { values, total };
  }, [paymentColumns, amortizationRow]);

  // Helper to get actual value from custom payment
  const getActualValueFromCustomPayment = useCallback(
    (columnKey: keyof AmortizationRow): number => {
      if (!actualPayment) return 0;

      // Map column keys to custom payment fields
      if (columnKey === "principal_payment") return actualPayment.principal_amount;
      if (columnKey === "interest_payment") return actualPayment.interest_amount;
      if (columnKey === "vat_payment") return actualPayment.vat_amount || 0;
      if (columnKey === "principal_indexation_payment") return actualPayment.principal_indexation_amount || 0;
      if (columnKey === "fees_payment") return actualPayment.fee_amount || 0;

      return 0;
    },
    [actualPayment]
  );

  const initialValues: PaymentFormData = useMemo(() => {
    const currency = actualPayment?.currency || dealCurrency || "USD";

    // Calculate actual total from custom payment if it exists (only for diff mode)
    let actualTotal = 0;
    if (mode === "diff" && actualPayment) {
      paymentColumns.forEach((column) => {
        actualTotal += getActualValueFromCustomPayment(column.key as keyof AmortizationRow);
      });
    }

    const formData: PaymentFormData = {
      effectiveDate: amortizationRow?.date || "",
      currency,
      totalAmount: mode === "approve" ? expectedValues.total : actualPayment ? actualTotal : 0
    };

    // Add expected and actual values for each payment column
    paymentColumns.forEach((column) => {
      const expectedValue = expectedValues.values[column.key] || 0;

      // For approve mode, actual always equals expected
      // For diff mode with custom payment, use custom payment values
      // For diff mode without custom payment, start at 0
      const actualValue =
        mode === "approve"
          ? expectedValue
          : actualPayment
            ? getActualValueFromCustomPayment(column.key as keyof AmortizationRow)
            : 0;

      formData[`expected_${column.key}`] = expectedValue;
      formData[`actual_${column.key}`] = actualValue;
    });

    return formData;
  }, [
    amortizationRow,
    dealCurrency,
    paymentColumns,
    expectedValues,
    mode,
    actualPayment,
    getActualValueFromCustomPayment
  ]);

  const formik = useFormik<PaymentFormData>({
    initialValues,
    onSubmit: async () => {
      if (formik.values.totalAmount === 0) {
        await createPayment();
      } else {
        setIsValuesDrawerOpen(true);
      }
    }
  });

  // Format number with user's number format settings
  const formatNumber = useCallback(
    (value: number): string => {
      return formatNumeric({ value });
    },
    [formatNumeric]
  );

  // Auto-allocate total amount across payment columns (only for diff mode)
  useEffect(() => {
    if (mode === "diff") {
      const total = formik.values.totalAmount;
      let remaining = total;

      // Allocate to each payment column in order
      paymentColumns.forEach((column, index) => {
        const expectedValue = expectedValues.values[column.key] || 0;
        const allocated = Math.min(expectedValue, remaining);
        remaining -= allocated;

        // If this is the last column and there's overpayment, add it here
        const isLastColumn = index === paymentColumns.length - 1;
        const finalAllocated = isLastColumn ? allocated + remaining : allocated;

        formik.setFieldValue(`actual_${column.key}`, finalAllocated);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, formik.values.totalAmount, paymentColumns, expectedValues]);

  const shortfall = useMemo(() => {
    return paymentColumns.reduce((total, column) => {
      const expected = expectedValues.values[column.key] || 0;
      const actual = formik.values[`actual_${column.key}`] || 0;
      return total + (expected - actual);
    }, 0);
  }, [paymentColumns, expectedValues, formik.values]);

  const handleValuesDrawerClose = (updated?: boolean) => {
    setIsValuesDrawerOpen(false);
    onClose(updated);
  };

  // Helper to get actual value for a specific column key
  const getActualValue = (key: keyof AmortizationRow) => formik.values[`actual_${key}`] || 0;

  const prepaymentFormData: PrepaymentFormData = useMemo(
    () => ({
      prepaymentType: "partial",
      effectiveDate: formik.values.effectiveDate,
      currency: formik.values.currency,
      refinanceDealName: "",
      setManual: false,
      totalAmount: formik.values.totalAmount,
      principalAmount: getActualValue("principal_payment"),
      interestAmount: getActualValue("interest_payment"),
      vatAmount: getActualValue("vat_payment"),
      vatRate: 0.18,
      creditFacilityFeeAmount:
        getActualValue("fees_payment") - (prepayment?.fees.reduce((total, fee) => total + fee.amount, 0) || 0),
      includeInterest: true,
      includeVAT: true,
      includeIndexation: false,
      principalId: (actualPayment || prepayment)?.principal_measure_data_id || null,
      interestId: (actualPayment || prepayment)?.interest_measure_data_id || null,
      vatId: (actualPayment || prepayment)?.vat_measure_data_id || null,
      principalIndexationId: (actualPayment || prepayment)?.principal_indexation_measure_data_id || null,
      pikInterestId: (actualPayment || prepayment)?.pik_interest_measure_data_id || null,
      includePikInterest: !!(actualPayment || prepayment)?.pik_interest_amount,
      pikInterestAmount: (actualPayment || prepayment)?.pik_interest_amount || 0,
      creditFacilityFeeId: (actualPayment || prepayment)?.fee_measure_data_id || null,
      fees:
        prepayment?.fees?.map((fee) => ({
          id: fee.id.toString(),
          type: fee.type as FeeType,
          percentage: fee.is_percentage ? fee.amount : 0,
          amount: fee.is_percentage ? 0 : fee.amount,
          measure_data_id: fee.fee_measure_data_id
        })) || [],
      feeSetManual: false
    }),
    [formik.values, actualPayment, prepayment]
  );

  const createPayment = async (): Promise<CustomPayment> => {
    if (actualPayment) {
      // Update existing custom payment
      const updatedPayment: PrepaymentPayload = {
        id: actualPayment.id,
        date: formik.values.effectiveDate,
        is_principal_percentage: false,
        principal_amount: getActualValue("principal_payment"),
        interest_amount: getActualValue("interest_payment"),
        vat_amount: getActualValue("vat_payment"),
        principal_indexation_amount: getActualValue("principal_indexation_payment") || null,
        fee_amount: getActualValue("fees_payment") || null,
        fee_measure_data_id: actualPayment.fee_measure_data_id,
        currency: formik.values.currency,
        is_actual: true,
        shortfall
      };

      const responsePayment = await updateCustomPayment(dealId, actualPayment.id, updatedPayment);
      return responsePayment;
    } else {
      // Create new custom payment
      const payload: PrepaymentPayload = {
        date: formik.values.effectiveDate,
        principal_amount: getActualValue("principal_payment"),
        interest_amount: getActualValue("interest_payment"),
        vat_amount: getActualValue("vat_payment"),
        principal_indexation_amount: getActualValue("principal_indexation_payment"),
        fee_amount: getActualValue("fees_payment") || null,
        is_principal_percentage: false,
        fees: [],
        currency: formik.values.currency,
        is_actual: true,
        shortfall
      };

      const responsePrepayment = await submitPrepayment(dealId, payload);
      return responsePrepayment;
    }
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Stack sx={{ p: 5, height: "calc(100vh - 50px)" }}>
          <Stack spacing={3} sx={{ overflowY: "auto", overflowX: "hidden", flex: 1 }}>
            {/* General Section */}
            <Stack spacing={2} gap={2} paddingTop={2}>
              <TextField
                name="prepaymentType"
                label="Prepayment Type"
                value="Partial Payment"
                disabled
                fullWidth
                size="small"
                sx={textFieldStyles}
              />

              <Box sx={{ display: "grid", gridTemplateColumns: "4fr 1fr", gap: 3 }}>
                <DatePicker
                  name="effectiveDate"
                  label="Effective Date"
                  format={dateFormat}
                  views={["year", "month", "day"]}
                  value={formik.values.effectiveDate ? dayjs(formik.values.effectiveDate) : null}
                  disabled
                  sx={{ width: "100%", ...inputStyles }}
                />

                <TextField
                  name="currency"
                  label="Currency"
                  value={formik.values.currency}
                  disabled
                  fullWidth
                  size="small"
                  sx={textFieldStyles}
                />
              </Box>
            </Stack>

            {/* Total Sum Section - Different behavior for approve vs diff */}
            <Stack spacing={2}>
              <Box sx={titleStyles}>
                <Typography variant="h6" fontSize={14}>
                  {mode === "approve" ? "Expected Payment" : "Actual Payment"}
                </Typography>
              </Box>
              <NumberFormat
                name="totalAmount"
                label="Total sum"
                thousandSeparator
                isNumericString
                variant="outlined"
                prefix={currencySign[formik.values.currency as CurrencyType]}
                decimalScale={2}
                allowNegative={false}
                customInput={TextField}
                value={formik.values.totalAmount}
                disabled={mode === "approve"}
                onValueChange={({ value }) => {
                  if (mode === "diff") {
                    formik.setFieldValue("totalAmount", parseFloat(value) || 0);
                  }
                }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: mode === "diff" ? "white" : "transparent"
                  },
                  ...inputStyles
                }}
              />
            </Stack>

            {/* Calculate Section */}
            <Stack spacing={2}>
              <Box
                sx={{
                  borderBottom: "1px solid",
                  position: "relative",
                  mb: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    bgcolor: "background.paper",
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "text.secondary"
                  }}
                >
                  <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                  <Typography variant="body2">Calculate</Typography>
                  <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                </Box>
              </Box>

              <Box>
                {mode === "diff" && (
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    Expected / Actual
                  </Typography>
                )}
                <PaymentRowsStack
                  currency={formik.values.currency}
                  paymentColumns={paymentColumns}
                  formValues={formik.values}
                  mode={mode}
                  formatNumber={formatNumber}
                />
              </Box>
            </Stack>

            {/* Shortfall/Total Sum Section */}
            <Stack spacing={2}>
              <Box sx={titleStyles}>
                <Typography variant="h6" fontSize={14}>
                  {mode === "approve" ? "Total sum" : shortfall < 0 ? "Overpayment" : "Shortfall"}
                </Typography>
              </Box>
              <NumberFormat
                name={mode === "approve" ? "finalTotal" : "shortfall"}
                label={mode === "approve" ? "Sum" : shortfall < 0 ? "Total Overpayment" : "Total Shortfall"}
                thousandSeparator
                isNumericString
                variant="outlined"
                prefix={currencySign[formik.values.currency as CurrencyType]}
                decimalScale={2}
                disabled
                customInput={TextField}
                value={mode === "approve" ? formik.values.totalAmount : Math.abs(shortfall)}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "transparent"
                  },
                  ...inputStyles
                }}
              />
            </Stack>
          </Stack>
          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}>
            <Button variant="outlined" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={submitting || updating}>
              {formik.values.totalAmount === 0 ? "Create" : "Next"}
            </Button>
          </Box>
        </Stack>
      </form>

      {isValuesDrawerOpen && rounds && (
        <PrepaymentDrawerValues
          rounds={rounds}
          open={isValuesDrawerOpen}
          onClose={handleValuesDrawerClose}
          dealId={dealId}
          createPrepayments={createPayment}
          prepaymentFormData={prepaymentFormData}
        />
      )}
    </>
  );
};

export const PaymentConfirmationDrawer: React.FC<PaymentConfirmationDrawerProps> = (props) => {
  const title = props.mode === "approve" ? "Confirm Payment" : "Apply Changed Payment";

  return (
    <Drawer anchor="left" open={props.open} handleClose={() => props.onClose()} title={title}>
      {props.dealId && (
        <AsyncContentMui isLoading={false}>
          <Content {...props} mode={props.mode} />
        </AsyncContentMui>
      )}
    </Drawer>
  );
};
